import React, { useEffect, useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Customized,
} from 'recharts';
import { Star } from 'lucide-react';
import { formatCompactCurrency, formatCurrency } from '../../../utils/formatters';

const DREAM_TOOLTIP_FIXED_Y = 25;
const DREAM_TOOLTIP_SHIFT_DOWN = 20;
const MAIN_TOOLTIP_FIXED_Y = 100;

const DreamTooltipLayer = ({ tooltip }) => {
  if (!tooltip) return null;

  const { x, y, lines, color } = tooltip;
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  if (!lines || !Array.isArray(lines) || lines.length === 0) return null;

  const textColor = color || '#143145';

  const tooltipPaddingX = 10;
  const tooltipPaddingY = 8;
  const lineHeight = 16;
  const tooltipWidth = 200;
  const tooltipHeight = tooltipPaddingY * 2 + lines.length * lineHeight;

  const tooltipX = x + 12;
  const tooltipY = y - tooltipHeight - 8 + DREAM_TOOLTIP_SHIFT_DOWN;
  const safeTooltipY = Math.max(2, tooltipY);

  return (
    <g pointerEvents="none">
      <rect
        x={tooltipX}
        y={safeTooltipY}
        width={tooltipWidth}
        height={tooltipHeight}
        rx={4}
        ry={4}
        fill="white"
        stroke="#D1D5DB"
      />
      <text
        x={tooltipX + tooltipPaddingX}
        y={safeTooltipY + tooltipPaddingY + 12}
        fill={textColor}
        className="font-medium text-sm"
      >
        {lines.map((t, idx) => (
          <tspan key={idx} x={tooltipX + tooltipPaddingX} dy={idx === 0 ? 0 : lineHeight}>
            {t}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const CustomDotWithDreams = ({ cx, cy, payload, dataKey, onDreamHover, onDreamLeave }) => {
  const yTop = DREAM_TOOLTIP_FIXED_Y;

  const hitR = 14;
  const color = '#143145';

  const iconSize = 16;

  if (typeof cx !== 'number' || typeof cy !== 'number') return null;
  if (!payload || !dataKey) return null;

  const row = payload && payload.payload ? payload.payload : payload;

  const timelineKey = `${dataKey}_timeline`;
  const timeline = row[timelineKey];
  const ageDecimal = row.ageDecimal;

  if (!timeline || !Array.isArray(timeline)) return null;
  if (typeof ageDecimal !== 'number') return null;

  const point = timeline.find(
    (p) => p && typeof p.ageDecimal === 'number' && Math.abs(p.ageDecimal - ageDecimal) < 0.01
  );

  if (!point || !point.dreams || point.dreams.length === 0) return null;

  const hasNegativeDream = point.dreams.some((dream) => dream.value < 0 || dream.dreamType !== 'contribution');

  const starColor = hasNegativeDream ? '#B76955' : '#39B1B6';

  const lines = useMemo(() => {
    return point.dreams.map((dream) => {
      const sign = dream.dreamType === 'contribution' ? '+' : '-';
      return `${dream.name}: ${sign}${formatCurrency(dream.value)}`;
    });
  }, [point.dreams]);

  const handleEnter = () => {
    if (typeof onDreamHover === 'function') {
      onDreamHover({ x: cx, y: yTop, lines, color: starColor });
    }
  };

  const handleLeave = () => {
    if (typeof onDreamLeave === 'function') onDreamLeave();
  };

  return (
    <g>
      <line x1={cx} y1={cy} x2={cx} y2={yTop} stroke={starColor} strokeWidth={1} strokeDasharray="4 2" />

      {/* hit area */}
      <circle
        cx={cx}
        cy={yTop}
        r={hitR}
        fill="transparent"
        style={{ cursor: 'pointer' }}
        onMouseEnter={handleEnter}
        onMouseMove={handleEnter}
        onMouseLeave={handleLeave}
      />

      <foreignObject
        x={cx - iconSize / 2}
        y={yTop - iconSize / 2}
        width={iconSize}
        height={iconSize}
        style={{ pointerEvents: 'none' }}
      >
        <Star size={iconSize} fill={starColor} color="white" strokeWidth={2} />
      </foreignObject>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const formattedAge = `${Math.floor(label)} anos e ${Math.round((label % 1) * 12)} meses`;

  return (
    <div className="bg-white p-3 rounded shadow text-sm text-blueDark border border-gray-300">
      {payload.map((entry, i) => {
        const dataKey = entry.dataKey;
        const timeline = (entry.payload && entry.payload[`${dataKey}_timeline`]) || [];
        const point = timeline.find((p) => Math.abs(p.ageDecimal - label) < 0.01);

        return (
          <div key={i} className="mb-2">
            <div className="font-medium" style={{ color: entry.color }}>
              <span>{payload[i] && payload[i].name}</span>
            </div>

            <div className="font-medium" style={{ color: entry.color }}>
              <span>Idade: {formattedAge}</span>
            </div>

            <div className="font-medium" style={{ color: entry.color }}>
              <span>Patrimônio: {formatCurrency(entry.value)}</span>
            </div>

            {point?.income !== undefined && (
              <div className="font-medium">
                <span>Rendimento: {formatCurrency(point.income)}</span>
              </div>
            )}
            {point?.contribution !== undefined && (
              <div className="font-medium">
                <span>Aporte: {formatCurrency(point.contribution)}</span>
              </div>
            )}
            {point?.consume !== undefined && (
              <div className="font-medium">
                <span>Retirada: {formatCurrency(point.consume)}</span>
              </div>
            )}
            {point && point.dreams && point.dreams.length > 0 && (
              <div className="font-medium">
                {point.dreams.map((dream, idx) =>
                  dream.dreamType === 'contribution' ? (
                    <div key={idx}>
                      {dream.name}: +{formatCurrency(dream.value)}
                    </div>
                  ) : (
                    <div key={idx}>
                      {dream.name}: -{formatCurrency(dream.value)}
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const RetirementProjectionChart = ({ scenarios, scenarioColorMap = {} }) => {
  const [dreamTooltip, setDreamTooltip] = useState(null);

  useEffect(() => {
    scenarios.forEach((s, i) => {});
  }, [scenarios]);

  if (!scenarios || scenarios.length === 0) return null;

  // Conjunto de datas (mês a mês)
  const allDates = new Set();
  scenarios.forEach((s) => {
    (s.timeline || []).forEach((point) => {
      if (point && point.monthDate) allDates.add(point.monthDate);
    });
  });

  const sortedDates = [...allDates].sort();

  // Unifica dados por monthDate
  const mergedData = sortedDates.map((monthDate) => {
    const entry = { monthDate };
    scenarios.forEach((s) => {
      const point = (s.timeline || []).find((p) => p && p.monthDate === monthDate);
      if (point) {
        entry[`scenario_${s.scenarioId}`] = point.amount;
        entry[`scenario_${s.scenarioId}_timeline`] = s.timeline;
        entry.ageDecimal = point.ageDecimal;
      }
    });
    return entry;
  });

  return (
    <div style={{ width: '100%', height: 360 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={mergedData} margin={{ top: 20, right: 40, left: 0, bottom: 25 }}>
          <CartesianGrid strokeDasharray="0" stroke="#EBEBEB" vertical={false} strokeWidth={2} />

          <XAxis
            dataKey="ageDecimal"
            tickFormatter={(val) => `${Math.floor(val)}`}
            tick={{ dy: 10, style: { fill: '#2D536E', fontSize: 16, fontWeight: 400 } }}
            stroke="#4a5568"
            fontSize={12}
            interval="preserveStartEnd"
            axisLine={false}
            tickLine={false}
            label={{
              value: 'IDADE',
              position: 'insideBottom',
              offset: -25,
              style: { textAnchor: 'middle', fill: '#39B1B6', fontSize: 14, fontWeight: 500 },
            }}
          />

          <YAxis
            tickFormatter={formatCompactCurrency}
            tick={{ dx: -10, style: { fill: '#2D536E', fontSize: 18, fontWeight: 600 } }}
            stroke="#4a5568"
            fontSize={12}
            width={100}
            axisLine={false}
            tickLine={false}
            label={{
              value: 'PATRIMÔNIO',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { textAnchor: 'middle', fill: '#39B1B6', fontSize: 14, fontWeight: 500 },
            }}
          />

          <Tooltip content={<CustomTooltip />} position={{ y: MAIN_TOOLTIP_FIXED_Y }} />

          {scenarios.length > 1 && (
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              wrapperStyle={{ paddingLeft: 20, fontSize: 14, color: '#2D536E' }}
            />
          )}

          {[...scenarios].reverse().map((s, idx) => {
            const color = scenarioColorMap[s.scenarioId] || '#3084B2';

            const dot =
              idx === 0 ? (
                <CustomDotWithDreams
                  dataKey={`scenario_${s.scenarioId}`}
                  onDreamHover={setDreamTooltip}
                  onDreamLeave={() => setDreamTooltip(null)}
                />
              ) : (
                false
              );

            return (
              <Area
                key={s.scenarioId}
                type="monotone"
                dataKey={`scenario_${s.scenarioId}`}
                name={s.scenarioName || `Cenário ${s.scenarioId}`}
                stroke={color}
                fill={color}
                fillOpacity={0.7}
                strokeWidth={1}
                strokeOpacity={1}
                isAnimationActive={false}
                dot={dot}
              />
            );
          })}

          <Customized
            component={(rechartsProps) =>
              dreamTooltip ? <DreamTooltipLayer tooltip={dreamTooltip} {...rechartsProps} /> : null
            }
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RetirementProjectionChart;
